import { ChangeDetectionStrategy, Component, computed, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CreateLoginsPhoneRequest,
  LoginsApi,
  LoginsPhoneModel,
  PhonesApi,
  SettingsApi,
  UpdateGlobalPhoneStatusRequest,
  UpdatePhoneStatusRequestRequest,
  UpdateProviderRequest,
} from 'src/app/hickory-api';
import { MatSnackBar } from '@angular/material/snack-bar';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { catchError, finalize, tap, throwError } from 'rxjs';
import { PhoneNumberHelper } from 'src/app/shared/phone-numbers/phone-number.helper';
import { AccessControl } from 'src/app/shared/authentication/access-control';
import { deepCopy } from 'src/app/shared/model/utility';
import { ContactPhonesHelper } from 'src/app/contacts/contact-edit/contact-phones/contact-phones.helper';

@Component({
  selector: 'app-phones-detail-modal',
  templateUrl: './phones-detail-modal.component.html',
  styleUrl: './phones-detail-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PhonesDetailModalComponent implements OnInit {
  phoneTypes = signal<CreateLoginsPhoneRequest.TypeEnum[]>([]);

  hasLoaded = signal<boolean>(true);

  readonly currentPhoneType = signal<CreateLoginsPhoneRequest.TypeEnum | undefined>(undefined);
  readonly currentPhoneNumber = signal<string>('');
  readonly currentProvider = signal<string | undefined>(undefined);
  readonly currentVoiceStatus = signal<UpdatePhoneStatusRequestRequest.NewStatusEnum | undefined>(undefined);
  readonly currentSmsStatus = signal<UpdatePhoneStatusRequestRequest.NewStatusEnum | undefined>(undefined);

  readonly isEditing = computed(
    () =>
      this.currentProvider() !== this.data.model.provider ||
      this.currentVoiceStatus() !== this.data.model.voiceStatus ||
      this.currentSmsStatus() !== this.data.model.smsStatus,
  );
  readonly isInvalidPhone = computed(() => !PhoneNumberHelper.isValid(this.currentPhoneNumber()));
  readonly isPhoneTypeSelected = computed(() => !!this.currentPhoneType());

  readonly canSavePhone = computed(() => {
    // Must wait for data to load
    if (!this.hasLoaded()) {
      return false;
    }

    // BackOffice mode bypasses validation (existing behavior)
    if (this.isBackOfficeMode) {
      return true;
    }

    // Require both valid phone AND phone type
    return !this.isInvalidPhone() && this.isPhoneTypeSelected();
  });

  readonly isSmsStatusUnsupported = computed(() => this.currentSmsStatus() === ('Unsupported' as any));
  readonly isVoiceStatusUnsupported = computed(() => this.currentVoiceStatus() === ('Unsupported' as any));

  typeNameMap = ContactPhonesHelper.typeNameMap;

  get statusNameMap() {
    return {
      [UpdatePhoneStatusRequestRequest.NewStatusEnum.Active]: this.translation.translate(
        'Contacts.Management.Phones.Status.Active',
        {},
        this.locale.language,
      ),
      [UpdatePhoneStatusRequestRequest.NewStatusEnum.Inactive]: this.translation.translate(
        'Contacts.Management.Phones.Status.Inactive',
        {},
        this.locale.language,
      ),
      [UpdatePhoneStatusRequestRequest.NewStatusEnum.UnreachablePhoneNumber]: this.translation.translate(
        'Contacts.Management.Phones.Status.UnreachablePhoneNumber',
        {},
        this.locale.language,
      ),
      [UpdatePhoneStatusRequestRequest.NewStatusEnum.Unsubscribed]: this.translation.translate(
        'Contacts.Management.Phones.Status.Unsubscribed',
        {},
        this.locale.language,
      ),
    };
  }

  providers = signal<any[]>([]);

  constructor(
    public dialogRef: MatDialogRef<PhonesDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PhonesModalData,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    public translation: L10nTranslationService,
    public loginsApi: LoginsApi,
    public phonesApi: PhonesApi,
    public snackBar: MatSnackBar,
    public authService: AuthService,
    private readonly settingsApi: SettingsApi,
  ) {}

  get canEditProvider() {
    return this.data.action !== 'View';
  }

  get canEditPhone() {
    return this.data.action !== 'View';
  }

  get isEditMode() {
    return this.data.action === 'Edit';
  }

  get isBackOfficeMode() {
    return this.data.action === 'BackOffice';
  }

  get isSuperAdmin() {
    return this.authService.isSuperAdmin;
  }

  get hasProvider(): boolean {
    return !!this.data.model.provider;
  }

  get shortenedPhone() {
    if (this.data.model.number) {
      return this.data.model.number.substring(2);
    }
    return this.data.model.number;
  }

  get supportsSms() {
    if (!this.data.model?.type) {
      return this.isBackOfficeMode;
    }

    return this.isBackOfficeMode || ContactPhonesHelper.supportsSms(this.data.model?.type);
  }

  get supportsVoice() {
    if (!this.data.model?.type) {
      return this.isBackOfficeMode;
    }

    return this.isBackOfficeMode || ContactPhonesHelper.supportsVoice(this.data.model?.type);
  }

  ngOnInit(): void {
    this.getProviders();
    this.phoneTypes.set(this.data.possibleTypes);
    this.currentPhoneNumber.set(this.shortenedPhone ?? '');
    this.currentPhoneType.set(this.data.model.type);
    this.currentProvider.set(this.data.model.provider);
    this.currentVoiceStatus.set(
      this.data.model.voiceStatus as UpdatePhoneStatusRequestRequest.NewStatusEnum | undefined,
    );
    this.currentSmsStatus.set(this.data.model.smsStatus as UpdatePhoneStatusRequestRequest.NewStatusEnum | undefined);
  }

  onSaveClick(): void {
    if (!this.currentPhoneType && this.phoneTypes.length > 0) {
      this.snackBar.open(
        this.translation.translate('Contacts.Management.Phones.Snackbar.SelectPhoneType', {}, this.locale.language),
        undefined,
        {
          duration: 5000,
        },
      );
      return;
    }

    if (this.currentPhoneNumber()?.length != 10) {
      this.snackBar.open(
        this.translation.translate('Contacts.Management.Phones.Snackbar.InvalidPhoneNumber', {}, this.locale.language),
        undefined,
        {
          duration: 5000,
        },
      );
      return;
    }

    if (this.data.action == 'Create') {
      this.addPhoneNumber();
    }

    const model = deepCopy(this.data.model);

    if (this.isEditMode || this.isBackOfficeMode) {
      if (this.currentSmsStatus() !== this.data.model.smsStatus) {
        const statusEnum = this.currentSmsStatus();
        model.smsStatus = statusEnum;
        this.updateSubscription(statusEnum!, UpdatePhoneStatusRequestRequest.ChannelTypeEnum.Sms);
      }

      if (this.currentVoiceStatus() !== this.data.model.voiceStatus) {
        const statusEnum = this.currentVoiceStatus();
        model.voiceStatus = statusEnum;
        this.updateSubscription(statusEnum!, UpdatePhoneStatusRequestRequest.ChannelTypeEnum.Voice);
      }

      if (this.currentProvider() !== this.data.model.provider) {
        model.provider = this.currentProvider();
        this.updateProvider();
      }

      this.dialogRef.close(model);
    }
  }

  updateProvider(): void {
    if (!this.data.userId) {
      return;
    }

    const request: UpdateProviderRequest = {
      providerCode: this.currentProvider(),
    };

    this.hasLoaded.set(false);
    this.loginsApi
      .updateProvider(this.data.userId, this.currentPhoneNumber()!, request)
      .pipe(
        finalize(() => this.hasLoaded.set(true)),
        tap(() =>
          this.snackBar.open(
            this.translation.translate('Contacts.Management.Phones.Snackbar.ProviderUpdated', {}, this.locale.language),
            undefined,
            { duration: 5000 },
          ),
        ),
        catchError(() => {
          const errorMessage = this.translation.translate(
            'Contacts.Management.Phones.Snackbar.ProviderUpdateError',
            {},
            this.locale.language,
          );
          this.snackBar.open(errorMessage, undefined, {
            duration: 5000,
          });
          return throwError(() => new Error(errorMessage));
        }),
      )
      .subscribe();
  }

  addPhoneNumber(): void {
    if (!this.data.userId) {
      this.dialogRef.close({
        number: '+1' + this.currentPhoneNumber(),
        type: this.getEnumFromString(this.currentPhoneType()!) as CreateLoginsPhoneRequest.TypeEnum,
        isPrimary: this.currentPhoneType() == CreateLoginsPhoneRequest.TypeEnum.Cell,
      });
      return;
    }

    const request: CreateLoginsPhoneRequest = {
      number: '+1' + this.currentPhoneNumber(),
      type: this.getEnumFromString(this.currentPhoneType()!) as CreateLoginsPhoneRequest.TypeEnum,
      isPrimary: this.currentPhoneType() == CreateLoginsPhoneRequest.TypeEnum.Cell,
      activateImmediately: true,
    };

    this.hasLoaded.set(false);

    this.loginsApi
      .createLoginsPhones(this.data.userId, request)
      .pipe(finalize(() => this.hasLoaded.set(true)))
      .subscribe({
        next: (result) => {
          this.dialogRef.close(result.item);
        },
        error: () => {
          this.snackBar.open(
            this.translation.translate('Contacts.Management.Phones.Snackbar.AddError', {}, this.locale.language),
            undefined,
            {
              duration: 5000,
            },
          );
        },
      });
  }

  updateSubscription(
    newStatus: UpdatePhoneStatusRequestRequest.NewStatusEnum | undefined,
    channel: UpdatePhoneStatusRequestRequest.ChannelTypeEnum,
  ) {
    if (!newStatus) {
      return;
    }

    this.hasLoaded.set(false);

    const request: UpdatePhoneStatusRequestRequest | UpdateGlobalPhoneStatusRequest = {
      executeImmediateUpdate: true,
      newStatus: newStatus,
      channelType: channel,
    };

    if (channel == UpdatePhoneStatusRequestRequest.ChannelTypeEnum.Voice) {
      this.data.model.voiceStatus = newStatus;
    }

    const errorMessage = this.translation.translate(
      'Contacts.Management.Phones.Snackbar.StatusUpdateError',
      {},
      this.locale.language,
    );
    const successMessage = this.translation.translate(
      'Contacts.Management.Phones.Snackbar.StatusUpdated',
      {},
      this.locale.language,
    );
    if (this.data?.userId) {
      this.loginsApi
        .updatePhoneStatusRequest(this.data.userId, this.shortenedPhone ?? '', request)
        .pipe(
          finalize(() => this.hasLoaded.set(true)),
          tap(() => {
            this.snackBar.open(successMessage, undefined, {
              duration: 5000,
            });
          }),
          catchError(() => {
            this.snackBar.open(errorMessage, undefined, {
              duration: 5000,
            });
            return throwError(() => new Error(errorMessage));
          }),
        )
        .subscribe();
    } else {
      this.phonesApi
        .updateGlobalPhoneStatus(this.shortenedPhone ?? '', request)
        .pipe(
          finalize(() => this.hasLoaded.set(true)),
          tap(() => {
            this.snackBar.open(successMessage, undefined, {
              duration: 5000,
            });
          }),
          catchError(() => {
            this.snackBar.open(errorMessage, undefined, {
              duration: 5000,
            });
            return throwError(() => new Error(errorMessage));
          }),
        )
        .subscribe();
    }
  }

  getProviders(): void {
    this.settingsApi
      .getCellPhoneProviders(1, 250)
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
        tap((response: any) => {
          this.providers.set(response.items.sort((a: any, b: any) => a.CellPhoneDesc?.localeCompare(b?.CellPhoneDesc)));
        }),
      )
      .subscribe();
  }

  getEnumFromString(value: string | null): CreateLoginsPhoneRequest.TypeEnum | null {
    if (Object.values(CreateLoginsPhoneRequest.TypeEnum).includes(value as CreateLoginsPhoneRequest.TypeEnum)) {
      return value as CreateLoginsPhoneRequest.TypeEnum;
    }

    return null;
  }

  getValidPhoneStatuses(): Array<UpdatePhoneStatusRequestRequest.NewStatusEnum> {
    const validStatuses: Array<UpdatePhoneStatusRequestRequest.NewStatusEnum> = [];

    if (this.authService.hasPermission(AccessControl.AdminManage)) {
      validStatuses.push(UpdatePhoneStatusRequestRequest.NewStatusEnum.Active);
    }

    validStatuses.push(
      UpdatePhoneStatusRequestRequest.NewStatusEnum.Inactive,
      UpdatePhoneStatusRequestRequest.NewStatusEnum.UnreachablePhoneNumber,
      UpdatePhoneStatusRequestRequest.NewStatusEnum.Unsubscribed,
    );

    return validStatuses;
  }
}

export interface PhonesModalData {
  model: LoginsPhoneModel;
  possibleTypes: LoginsPhoneModel.TypeEnum[];
  userId?: number;
  action: 'Create' | 'Edit' | 'View' | 'BackOffice';
}
